import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { PortalTopNav } from '@/components/portal/PortalTopNav';
import { DemoModeBanner } from '@/components/portal/DemoModeBanner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from '@/components/Helmet';
import { demoDataProvider } from '@/demo/demoProvider';
import type { DemoDomain } from '@/demo/demoFixtures';
import { isDemoUserAllowed } from '@/demo/demoAccess';
import { NAV_LABELS } from '@/lib/vocabulary/securityTerminology';
import { formatTimestampSafe } from '@/lib/utils/formatTimestamp';

const formatTimestamp = (value?: string | null) => (value ? formatTimestampSafe(value) : '—');

const DemoDomainLayout = () => {
  const { siteId } = useParams();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { checking } = useRequireMfa();
  const navigate = useNavigate();
  const [domain, setDomain] = useState<DemoDomain | null>(null);

  const canAccess = useMemo(() => isDemoUserAllowed(user), [user]);

  useEffect(() => {
    if (!checking && !loading && user && !canAccess) {
      navigate('/portal', { replace: true });
    }
  }, [checking, loading, user, canAccess, navigate]);

  useEffect(() => {
    if (!siteId) return;
    let mounted = true;
    demoDataProvider.getDomain(siteId).then((data) => {
      if (mounted) setDomain(data);
    });
    return () => {
      mounted = false;
    };
  }, [siteId]);

  if (checking || loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading...</div>
      </div>
    );
  }

  if (!canAccess) {
    return null;
  }

  if (!domain) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Demo domain not found.</div>
      </div>
    );
  }

  const tabs = [
    { label: 'Overview', to: `/portal/demo/domains/${domain.site_id}` },
    { label: NAV_LABELS.protectionCoverage, to: `/portal/demo/domains/${domain.site_id}/coverage` },
    { label: NAV_LABELS.securityActivity, to: `/portal/demo/domains/${domain.site_id}/logs` },
    { label: NAV_LABELS.evidenceExport, to: `/portal/demo/domains/${domain.site_id}/evidence` },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>{domain.label} (Demo) | ShadowScan Portal</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <PortalTopNav
        userEmail={user.email || ''}
        userName={user.user_metadata?.full_name || undefined}
        active="demo"
      />

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-6xl space-y-6">
        <DemoModeBanner />

        <Card className="border-border/60">
          <CardContent className="flex flex-col gap-4 py-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-heading font-semibold">{domain.label}</h1>
                  <Badge variant="outline">DEMO</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{domain.domain}</p>
                <p className="text-xs text-muted-foreground">Last check-in: {formatTimestamp(domain.last_seen_at)}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/portal/demo')}>
                Back to Demo Workspace
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <Button
                  key={tab.to}
                  asChild
                  variant={location.pathname === tab.to ? 'secondary' : 'outline'}
                  size="sm"
                >
                  <NavLink to={tab.to}>{tab.label}</NavLink>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Outlet context={{ domain }} />
      </main>
    </div>
  );
};

export default DemoDomainLayout;
